// useSafePosthog.ts
import { useEffect, useRef, useCallback } from 'react'
import posthogClient from 'posthog-js'

export function useSafePosthog() {
  const networkSeenRef = useRef(false)
  const readyRef = useRef(false)

  /* ---------------------------
   * NETWORK PROBE (definitive)
   * --------------------------- */
  useEffect(() => {
    const _fetch = window.fetch
    window.fetch = function (...args) {
      try {
        if (args[0]?.toString?.().includes('/x/')) {
          networkSeenRef.current = true
        }
      } catch {}
      return _fetch.apply(this, args)
    }

    const _beacon = navigator.sendBeacon?.bind(navigator)
    if (_beacon) {
      navigator.sendBeacon = function (url, data) {
        if (url?.includes('/x/')) {
          networkSeenRef.current = true
        }
        return _beacon(url, data)
      }
    }

    // Restore not strictly required (init-only hook)
  }, [])

  /* ---------------------------
   * SDK READINESS CHECK
   * --------------------------- */
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        readyRef.current =
          posthogClient?.__loaded === true &&
          typeof posthogClient.capture === 'function' &&
          typeof posthogClient._send_request === 'function' &&
          typeof posthogClient.config === 'object'
      } catch {
        readyRef.current = false
      }

      if (!readyRef.current) {
        if (import.meta.env.PROD) {
          console.warn('[telemetry] PostHog not ready or blocked')
        }
      }
    }, 2000)

    return () => clearTimeout(t)
  }, [])

  /* ---------------------------
   * STUB DETECTION
   * --------------------------- */
  const isStubbed = useCallback(() => {
    try {
      if (!posthogClient?.capture) return true

      const src = Function.prototype.toString.call(posthogClient.capture)

      return src === 'function () {}' || src.length < 60 || src.includes('[native code]')
    } catch {
      return true
    }
  }, [])

  /* ---------------------------
   * SAFE CAPTURE
   * --------------------------- */
  const capture = useCallback(
    (event, props) => {
      try {
        if (!readyRef.current) return false
        if (isStubbed()) return false

        posthogClient.capture(event, props)
        return true
      } catch {
        return false
      }
    },
    [isStubbed],
  )

  /* ---------------------------
   * DIAGNOSTICS
   * --------------------------- */
  const diagnostics = {
    isReady: () => readyRef.current,
    hasNetworkActivity: () => networkSeenRef.current,
    isBlocked: () => !readyRef.current || isStubbed() || networkSeenRef.current === false,
  }

  return { capture, diagnostics }
}
